// src/components/wall/TransformMarketTableComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_ENDPOINT } from "../../../../../lib/api-url";

const ASSETS = {
  playerCard: "/transform-market/player-card.svg",
  playerCardSilver: "/transform-market/player-card-silver.svg",
  playerCardBoronze: "/transform-market/player-card-boronze.svg",
  priceColor: "#A22A2C",
  boronzePriceColor: "#303030",
  fallbackAvatar: "/avatar/avatar-default.webp",
};

// map API team names -> asset filenames (when API has no logo_path)
const TEAM_TO_LOGO = {
  GOLDENEAGLES: "goldeneagles-transform.svg",
  GOLDEN_EAGLES: "goldeneagles-transform.svg",
  BULLMASTERS: "bullmasters-transform-logo.svg",
  IRONBEARS: "ironbears-transform-market.svg",
  ALPHAWOLVES: "alphawolves-transform.svg",
};

// ---- exact animation you wanted ----
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      delayChildren: 0.28,
      staggerChildren: 0.16,
      when: "beforeChildren",
      ease: "easeOut",
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 18, filter: "blur(2px)" },
  visible: {
    opacity: 1,
    y: 0,
    filter: "blur(0px)",
    transition: { duration: 1.35, ease: [0.22, 1, 0.36, 1] },
  },
};

// helpers
const fullNameOf = (u = {}) => {
  const fn = (u.first_name || "").trim();
  const ln = (u.last_name || "").trim();
  const combined = `${fn} ${ln}`.trim();
  return combined || u.username || "—";
};
const parsePrice = (p) => {
  const n = Number(p);
  return Number.isFinite(n) ? n : 0;
};
const formatPrice = (n) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(n);

// prefer API's logo_path; else map by team name; else null (no logo)
const logoFromTeam = (user) => {
  const team = user?.team;
  if (!team) return null;
  if (team.logo_path) {
    return team.logo_path.startsWith("/")
      ? team.logo_path
      : `/transform-market/${team.logo_path}`;
  }
  const key = String(team.name || "")
    .toUpperCase()
    .replace(/\s+/g, "_");
  const mapped = TEAM_TO_LOGO[key];
  return mapped ? `/transform-market/${mapped}` : null;
};

const getCardSrcByIndex = (idx) => {
  if (idx < 5) return ASSETS.playerCard; // top 5
  if (idx >= 15) return ASSETS.playerCardBoronze; // last 5
  return ASSETS.playerCardSilver; // middle
};
const getPriceColorByIndex = (idx) =>
  idx >= 15 ? ASSETS.boronzePriceColor : ASSETS.priceColor;

export default function TransformMarketTableComponent({
  onDataReady,
  play, // parent controls when to animate the data grid
}) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const readyFiredRef = useRef(false);
  const [staggerSeed, setStaggerSeed] = useState(0); // bump to force re-mount

  // fetch + sort
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoading(true);
        const res = await axios.get(API_ENDPOINT.wallTransferMarket.getIndex);
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        const sorted = [...data].sort(
          (a, b) => parsePrice(b.price) - parsePrice(a.price)
        );
        if (mounted) setRows(sorted.slice(0, 20));
      } catch (e) {
        console.error(e);
        if (mounted) setErr("Failed to fetch transfer market.");
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  // build card view models
  const cards = useMemo(
    () =>
      rows.map((r, idx) => {
        const user = r.user || {};
        const userId = r.user_id ?? user.id ?? idx;
        const teamLogo = logoFromTeam(user); // null if no team

        return {
          key: userId,
          rank: idx + 1,
          name: fullNameOf(user),
          priceNum: parsePrice(r.price),
          priceText: formatPrice(parsePrice(r.price)),
          avatar: ASSETS.fallbackAvatar, // update if API adds avatars
          teamLogo, // may be null (=> we don't render an img)
          cardSrc: getCardSrcByIndex(idx),
          priceColor: getPriceColorByIndex(idx),
        };
      }),
    [rows]
  );

  // preload images; notify parent ONLY when data + assets are ready
  useEffect(() => {
    if (loading) return;
    if (readyFiredRef.current) return;

    const shouldRevealImmediately = !!err || cards.length === 0;
    if (shouldRevealImmediately) {
      readyFiredRef.current = true;
      setStaggerSeed((s) => s + 1); // still ensure mount key changes
      onDataReady?.();
      return;
    }

    const urls = new Set([
      ASSETS.playerCard,
      ASSETS.playerCardSilver,
      ASSETS.playerCardBoronze,
      ASSETS.fallbackAvatar,
      ...cards.map((c) => c.cardSrc),
      ...cards.map((c) => c.teamLogo).filter(Boolean),
    ]);

    const loadOne = (src) =>
      new Promise((resolve) => {
        const img = new Image();
        img.onload = img.onerror = () => resolve();
        img.src = src;
      });

    const timeout = new Promise((resolve) => setTimeout(resolve, 2500)); // safety cap

    (async () => {
      await Promise.race([Promise.all([...urls].map(loadOne)), timeout]);
      if (!readyFiredRef.current) {
        readyFiredRef.current = true;
        setStaggerSeed((s) => s + 1); // re-mount data container so stagger restarts
        onDataReady?.();
      }
    })();
  }, [loading, err, cards, onDataReady]);

  if (err) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="text-red-400 text-sm">{err}</div>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center mb-[42px] ">
      <AnimatePresence mode="wait">
        {loading ? (
          // ------------------ SKELETON GRID ------------------
          <motion.div
            key={`skeleton-${staggerSeed}`}
            className="h-full flex items-center justify-center gap-[32px] flex-wrap w-[830px]"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
          >
            {Array.from({ length: 20 }).map((_, idx) => (
              <motion.div
                key={`s-${idx}`}
                className="w-[118px] h-[165px] rounded-[10px] bg-slate-800/40"
                variants={itemVariants}
              />
            ))}
          </motion.div>
        ) : (
          // ------------------ DATA GRID ------------------
          <motion.div
            key={`data-${staggerSeed}`}
            className="h-full flex items-center justify-center gap-[32px] flex-wrap w-[830px]"
            variants={containerVariants}
            initial="hidden"
            animate={play ? "visible" : "hidden"} // parent controls start
            exit={{ opacity: 0, transition: { duration: 0.25 } }}
          >
            {cards.map((c) => (
              <motion.div
                key={c.key}
                className="w-[118px] relative will-change-transform transform-gpu"
                variants={itemVariants}
                layout
              >
                {/* card frame */}
                <img src={c.cardSrc} alt="player card" />

                {/* avatar */}
                <img
                  className="w-[62px] absolute z-[2] right-[20px] top-[18px]"
                  src={c.avatar}
                  alt="avatar"
                />

                {/* team logo — ONLY if user has a team */}
                {c.teamLogo ? (
                  <img
                    src={c.teamLogo}
                    className="w-[22px] absolute z-[2] left-[6px] bottom-[64px]"
                    alt="team logo"
                  />
                ) : null}

                {/* rank */}
                <div className="points-container flex flex-col absolute z-[2] left-[10px] top-[20px]">
                  <span
                    style={{ fontFamily: "Etemad" }}
                    className="text-[#892829] text-[26px] font-normal rank"
                  >
                    {c.rank}
                  </span>
                </div>

                {/* name + price */}
                <div className="flex flex-col absolute z-[2] left-[16px] bottom-[10px] gap-[2px] items-center justify-center">
                  <span
                    className="text-[#000000] text-[10px] font-bold truncate max-w-[86px]"
                    dir="auto"
                    title={c.name}
                  >
                    {c.name}
                  </span>
                  <span
                    style={{ fontFamily: "Etemad", color: c.priceColor }}
                    className="text-[15px] font-normal price"
                  >
                    {c.priceText}
                  </span>
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
